import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Text, theme } from '@dls/web';
import styled from 'styled-components';
import convert from 'htmr';
import { noop } from '@lux/helpers';

import Anchor from '../Anchor';
import { BodySecondary, H3, SubtitlePrimary } from '../Base';
import Button from '../Button';
import Ribbon from '../Ribbon';
import Spacing from '../Spacing';
import Tag, { TagColors, Tags } from '../Tag';

import formatPrice from '../../helpers/formatPrice';
import unlimitedTalktimeSms from '../../helpers/unlimited-talktime-sms';
import { planTagsConfig } from '../../config/plan-group-config';
import slugify from '../../helpers/slugify';
import colours from '../../theme/colours';
import useDevice from '@dls/web/build/core/hooks/useDevice';

const StyledCard = styled.div`
  background-color: ${p => p.theme.colours.white};
  display: flex;
  flex: 1 0;
  flex-direction: column;
  position: relative;
  width: 100%;
  border-radius: 16px;
  box-shadow: 0 1px 8px 0 rgba(0, 0, 0, 0.03),
    0 8px 23px 8px rgba(0, 0, 0, 0.05), 0 24px 27px -8px rgba(0, 0, 0, 0.08);

  ${p => p.theme.media.md`
    height: 100%;
    box-shadow: 0 8px 23px 8px rgba(0, 0, 0, 0.05);
  `};
`;

const StyledCardBody = styled.div`
  flex: 1 1 auto;
  padding: ${p => p.theme.spacing(3)} ${p => p.theme.spacing(2)};

  ${p => p.theme.media.md`
    padding: ${p.theme.spacing(3)};
  `};
`;

const StyledCardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${p => p.theme.spacing(2)};
  border-top: 1px solid ${p => p.theme.colours.grey_400};

  ${p =>
    p.addPaddingTop &&
    `
      padding: ${p.theme.spacing(2.8)} ${p.theme.spacing(2)};
  `};
`;

const StyledName = styled(BodySecondary)`
  color: ${p => p.theme.colours.grey_600};
  margin-bottom: ${p => p.theme.spacing(1)};
`;

const StyledListItem = styled.li`
  &:before {
    content: '✓';
    color: ${p => p.theme.colours.turqoise};
    margin-right: ${p => p.theme.spacing(1)};
  }
`;

const StyledUnorderedList = styled.ul`
  list-style: none;
  color: ${p => p.theme.colours.grey_600};
  margin: 0;
  padding: 0 0 0 ${p => p.theme.spacing(2.5)};
  text-indent: ${p => p.theme.spacing(-2.5)};

  li > p {
    display: inline;
  }
`;

const StyledButton = styled(Button)`
  width: ${p => p.theme.spacing(8)};
  height: ${p => p.theme.spacing(4)};

  ${p => p.theme.media.md`
    width: ${p.theme.spacing(11)};
    height: ${p.theme.spacing(6)};
  `};
`;

const StrikeThroughText = styled(Text)`
  text-decoration: line-through;
  display: inline;
  margin-left: 8px;
  ${props =>
    props.isMobile &&
    `
      display: block;
      margin-left: 0px;
  `}
  color: ${theme.main.colours.tertiary.l4};
`;

export const renderPlanTags = planTags => {
  let tags = [
    planTags.includes(planTagsConfig['5g-speed'].tagName)
      ? { text: '5G speed', color: TagColors.blue }
      : { text: '4G speed', color: TagColors.grey }
  ];

  return tags.map((tag, index) => {
    return (
      <Tag key={index} color={tag.color} data-testid="plan-tag">
        {tag.text}
      </Tag>
    );
  });
};

export const renderPrice = (
  price,
  discountPrice,
  discountedLiner,
  isMobile
) => {
  const parsedActualPrice = parseFloat(price);
  const parsedDiscountedPrice = parseFloat(discountPrice);
  if (parsedDiscountedPrice >= 0) {
    return (
      <div>
        <Text type="header" data-testid="discount-price">
          {`${formatPrice(parsedDiscountedPrice)}/mth`}
          {parsedDiscountedPrice < parsedActualPrice && (
            <StrikeThroughText
              type="smallBody"
              isMobile={isMobile}
              data-testid="price"
            >
              {`${formatPrice(parsedActualPrice)}`}
            </StrikeThroughText>
          )}
        </Text>
        <Text type="smallBody">{discountedLiner}</Text>
      </div>
    );
  } else {
    return <Text type="header">{`${formatPrice(parsedActualPrice)}/mth`}</Text>;
  }
};

/**
 * Format the plan data to a standardised format
 *
 * @param {Number} quantity
 * @param {String} unit
 * @returns {string}
 */
export const formatPlanData = (quantity, unit) => {
  return `${quantity}${unit}`.replace(/\s/, '');
};

const PlanCatalogCard = props => {
  const {
    name,
    ribbon,
    price,
    discountPrice,
    discountedLiner,
    data,
    talktime,
    tags = [],
    sms,
    planDescSubLiner,
    onPlanClick
  } = props;

  const { isMobile } = useDevice();

  const planSlug = useMemo(() => {
    return slugify(name);
  }, [name]);

  return (
    <StyledCard
      id={`PlanCard-${planSlug}`}
      data-testid={`PlanCard-${planSlug}`}
    >
      <StyledCardBody>
        <Ribbon>{ribbon}</Ribbon>
        <StyledName>{name}</StyledName>
        <Spacing bottom={1} css={{ display: 'flex', alignItems: 'center' }}>
          <Text type="sectionTitle02">
            {formatPlanData(data.quantity, data.unit)}
          </Text>{' '}
          {tags && <Tags css={{ marginLeft: 8 }}>{renderPlanTags(tags)}</Tags>}
        </Spacing>
        <BodySecondary as="div">
          <StyledUnorderedList>
            <StyledListItem>
              {unlimitedTalktimeSms({ talktime, sms })}
            </StyledListItem>
          </StyledUnorderedList>
          <StyledUnorderedList>
            {convert(`${planDescSubLiner}`, {
              transform: {
                a: Anchor,
                li: StyledListItem
              }
            })}
          </StyledUnorderedList>
        </BodySecondary>
      </StyledCardBody>
      <StyledCardFooter addPaddingTop={!discountPrice || !discountedLiner}>
        {renderPrice(price, discountPrice, discountedLiner, isMobile)}
        <StyledButton onClick={() => onPlanClick()}>Get</StyledButton>
      </StyledCardFooter>
    </StyledCard>
  );
};

PlanCatalogCard.defaultProps = {
  onPlanClick: noop
};

PlanCatalogCard.propTypes = {
  data: PropTypes.object,
  name: PropTypes.string,
  onPlanClick: PropTypes.func,
  planDescSubLiner: PropTypes.any,
  price: PropTypes.string,
  discountPrice: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  discountedLiner: PropTypes.string,
  ribbon: PropTypes.string,
  sms: PropTypes.object,
  tags: PropTypes.array,
  talktime: PropTypes.object
};

export default PlanCatalogCard;

const PriceTag = styled(H3)`
  display: inline-block;
  position: relative;
  top: 2px;
  color: ${({ theme }) => theme.colours.card_color_primary};
`;

const PriceSlash = styled(BodySecondary)`
  color: ${colours.grey_600};
  margin-top: -4px;
  > span {
    text-decoration: line-through;
  }
`;
